// Firebase configuration and initialization
import { initializeApp } from 'firebase/app';
import { getAuth, GoogleAuthProvider, signInAnonymously, signInWithPopup, signOut, onAuthStateChanged, User } from 'firebase/auth';
import { getFirestore, initializeFirestore, persistentLocalCache, persistentMultipleTabManager } from 'firebase/firestore';

// TODO: Replace with your actual Firebase config from Firebase Console
// Go to https://console.firebase.google.com/ and create a project
// Then go to Project Settings > General > Your apps > Add app (Web app)
// Copy the config object here
const firebaseConfig = {
  apiKey: "AIzaSyDUxfG-AXBia6wUgFC0GlccHvBOtMc4i8s",
  authDomain: "tc-custom-warband-builder.firebaseapp.com",
  projectId: "tc-custom-warband-builder",
  storageBucket: "tc-custom-warband-builder.firebasestorage.app",
  messagingSenderId: "97901177030",
  appId: "1:97901177030:web:511566b6e62fd9b2fe8fc7",
  measurementId: "G-333X6YPZ5C"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Firebase Authentication and get a reference to the service
export const auth = getAuth(app);

// Initialize Cloud Firestore with explicit persistence settings
let db: any;
try {
  // Try to initialize with persistent cache for better offline support
  db = initializeFirestore(app, {
    localCache: persistentLocalCache({
      tabManager: persistentMultipleTabManager()
    })
  });
} catch (error) {
  // Fallback to regular Firestore if persistence fails
  console.warn('Firestore persistence failed, using basic mode:', error);
  db = getFirestore(app);
}

export { db };

// Google Auth Provider
export const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({
  prompt: 'select_account'
});

// Add additional OAuth scopes if needed (optional)
// googleProvider.addScope('email');
// googleProvider.addScope('profile');

// Export auth methods
export { signInAnonymously, signInWithPopup, signOut, onAuthStateChanged };
export type { User };

// Global authentication state tracking
let currentUser: User | null = null;
let authStateInitialized = false;

// Initialize authentication state listener
onAuthStateChanged(auth, (user) => {
  console.log('🔐 [Firebase] Auth state changed:', user ? (user.isAnonymous ? 'Anonymous' : 'Google Auth') : 'Not authenticated');
  currentUser = user;
  authStateInitialized = true;

  // Dispatch custom event for components that need to react to auth changes
  window.dispatchEvent(new CustomEvent('authStateChanged', { detail: { user } }));
});

// Helper to get current user ID (safe even when not authenticated)
export const getCurrentUserId = (): string | null => {
  return currentUser?.uid || null;
};

// Helper to check if user is authenticated
export const isAuthenticated = (): boolean => {
  return currentUser !== null;
};

// Helper to check if user is anonymously authenticated
export const isAnonymous = (): boolean => {
  return currentUser?.isAnonymous || false;
};

// Helper to get current user object
export const getCurrentUser = (): User | null => {
  return currentUser;
};

// Helper to wait for authentication state to be initialized
export const waitForAuthState = (): Promise<User | null> => {
  return new Promise((resolve) => {
    if (authStateInitialized) {
      resolve(currentUser);
      return;
    }

    const handleAuthState = (event: CustomEvent) => {
      window.removeEventListener('authStateChanged', handleAuthState as EventListener);
      resolve(event.detail.user);
    };

    window.addEventListener('authStateChanged', handleAuthState as EventListener);
  });
};

// Utility function to clear Firebase cache and IndexedDB data
export const clearFirebaseCache = async (): Promise<void> => {
  console.log('🧹 [Firebase] Clearing Firebase cache...');

  try {
    // Clear localStorage Firebase data
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('firebase') ||
                  key.startsWith('firestore') ||
                  key.startsWith('firestore_') ||
                  key.includes('firebase'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => {
      console.log(`🗑️ [Firebase] Removing localStorage key: ${key}`);
      localStorage.removeItem(key);
    });

    // Clear IndexedDB databases
    if ('indexedDB' in window) {
      const databasesToDelete = [
        'firebase',
        'firestore',
        'firebase-local-storage',
        'firestore-persistence',
        'firestore_cache'
      ];

      for (const dbName of databasesToDelete) {
        try {
          console.log(`🗑️ [Firebase] Deleting IndexedDB: ${dbName}`);
          await new Promise<void>((resolve, reject) => {
            const deleteRequest = indexedDB.deleteDatabase(dbName);
            deleteRequest.onsuccess = () => resolve();
            deleteRequest.onerror = () => reject(deleteRequest.error);
            deleteRequest.onblocked = () => {
              console.warn(`⚠️ [Firebase] Delete blocked for database: ${dbName}`);
              resolve(); // Continue anyway
            };
          });
        } catch (error) {
          console.warn(`⚠️ [Firebase] Failed to delete database ${dbName}:`, error);
        }
      }
    }

    // Clear sessionStorage as well
    const sessionKeysToRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && (key.startsWith('firebase') || key.startsWith('firestore'))) {
        sessionKeysToRemove.push(key);
      }
    }
    sessionKeysToRemove.forEach(key => {
      console.log(`🗑️ [Firebase] Removing sessionStorage key: ${key}`);
      sessionStorage.removeItem(key);
    });

    console.log('✅ [Firebase] Cache clearing completed');

    // Give a moment for the clearing to take effect
    await new Promise(resolve => setTimeout(resolve, 500));

  } catch (error) {
    console.error('❌ [Firebase] Error clearing cache:', error);
    throw error;
  }
};

// Global function for manual cache clearing (accessible from browser console)
(window as any).clearFirebaseCache = clearFirebaseCache;